import { motion } from "framer-motion";
import {
  Scale,
  Megaphone,
  Users,
  FlaskConical,
  TrendingUp,
  Target,
  Handshake,
  Lightbulb,
  Rocket,
  MapPin,
  FileText,
  Search,
  BarChart3,
  Presentation,
  BookOpen,
  HandCoins,
  Newspaper,
  Radio,
  MessageSquare,
  Building2,
  ArrowRight,
  LucideIcon,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { useSiteContent } from "@/hooks/useSiteContent";
import { getBlock } from "@/lib/siteContent";
import aboutHeroBg from "@/assets/about-hero-bg.jpg";

interface Service {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface Practice {
  title: string;
  icon: LucideIcon;
  tagline: string;
  description: string;
  services: Service[];
}

const practices: Practice[] = [
  {
    title: "Lobbying",
    icon: Scale,
    tagline: "A trusted voice in the Alabama State House",
    description:
      "We monitor every bill that affects our clients, brief them on what's moving, and engage legislators directly. Our lobbying practice combines disciplined intelligence with the relationships that get a meeting on the calendar.",
    services: [
      { icon: FileText, title: "Bill Monitoring", description: "Real-time tracking of legislation across all 67 Alabama counties, filtered to the issues you care about." },
      { icon: Handshake, title: "Legislator Engagement", description: "Direct outreach, briefings, and meetings with members and staff on the committees that matter to your work." },
      { icon: Target, title: "Committee Strategy", description: "Testimony prep, amendment drafting, and floor strategy so your position is in the record at the right moment." },
    ],
  },
  {
    title: "Strategic Communication",
    icon: Megaphone,
    tagline: "Move the conversation, not just the message",
    description:
      "We listen across social and community channels, shape narrative around our clients' work, and earn coverage that gives policymakers the political cover to act.",
    services: [
      { icon: Radio, title: "Social Listening", description: "Track what Alabama communities are talking about across TikTok, Instagram, X, and local media to surface what's resonating." },
      { icon: MessageSquare, title: "Narrative Development", description: "Message architecture, talking points, and campaign-ready content that holds up under scrutiny." },
      { icon: Newspaper, title: "Earned Media", description: "Reporter relationships and pitch strategy that get our clients' stories into the outlets policymakers read." },
    ],
  },
  {
    title: "Advocacy",
    icon: Users,
    tagline: "Coalitions that turn pressure into policy",
    description:
      "Advocacy is a team sport. We build, organize, and activate cross-sector coalitions across Alabama — pairing grassroots energy with the grass-tops relationships that move outcomes.",
    services: [
      { icon: Handshake, title: "Coalition Building", description: "Stakeholder mapping, partnership development, and the convening work that gets unlikely allies to the same table." },
      { icon: Megaphone, title: "Grassroots Organizing", description: "Community mobilization, hearing turnout, and constituent campaigns that show legislators where their districts stand." },
      { icon: Presentation, title: "Stakeholder Convening", description: "Roundtables, summits, and working groups that align agencies, funders, and community leaders behind shared priorities." },
    ],
  },
  {
    title: "Research",
    icon: FlaskConical,
    tagline: "Evidence built to hold up under scrutiny",
    description:
      "Every position we take and every grant we write is grounded in research — county-level data, peer-reviewed studies, and rigorous policy analysis our clients can put their name on.",
    services: [
      { icon: Search, title: "Policy Research", description: "In-depth analysis of Alabama and federal legislation, cross-state benchmarking, and impact modeling." },
      { icon: MapPin, title: "County Data Analysis", description: "ACS, public health, education, and economic indicators mapped to the communities our clients serve." },
      { icon: BookOpen, title: "Issue Briefings", description: "Concise, citation-ready briefs that arm clients and allies for meetings, hearings, and media moments." },
    ],
  },
  {
    title: "Economic Development",
    icon: TrendingUp,
    tagline: "Move dollars into community-rooted work",
    description:
      "We design funding strategies and grant programs that connect Alabama capital — federal, state, philanthropic, and corporate — to the small, community-rooted organizations doing the work.",
    services: [
      { icon: HandCoins, title: "Funding Strategy", description: "Pipeline mapping, prioritization, and pursuit support across federal grants, state programs, and private capital." },
      { icon: Building2, title: "Grant Program Design", description: "Helping funders and agencies design programs that actually reach community-rooted applicants and produce evidence." },
      { icon: BarChart3, title: "Outcomes & ROI Analysis", description: "Measurement frameworks and reporting that prove the return on investment and protect the next funding cycle." },
    ],
  },
];

const pillars = [
  {
    icon: Target,
    title: "In the Room",
    description:
      "Our clients shouldn't have to choose between doing the work and showing up at the State House. We're there on their behalf — monitoring, briefing, and engaging.",
  },
  {
    icon: Users,
    title: "Built for the Underserved",
    description:
      "Most government affairs firms are built for clients with seven-figure budgets. We're built for the small, community-rooted organizations that need a partner most.",
  },
  {
    icon: Handshake,
    title: "Evidence over Hype",
    description:
      "Every position, every pitch, and every grant is backed by Alabama data and rigorous research. We make sure our clients' work holds up under any scrutiny.",
  },
];

const AboutUs = () => {
  const { blocks } = useSiteContent("about");
  const introP1 = getBlock(
    blocks,
    "intro_paragraph_1",
    "The Emerging Market is a government affairs firm headquartered in Alabama, specializing in lobbying, strategic communication, advocacy, research, and economic development. We were built for the small, community-rooted organizations that rarely have a partner in the room when decisions get made.",
  );
  const introP2 = getBlock(
    blocks,
    "intro_paragraph_2",
    "We pair traditional government affairs craft — bill tracking, legislator engagement, coalition building, message discipline — with the live data infrastructure you can browse on this site. Our clients use the same tools we do.",
  );
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-36 pb-20 relative overflow-hidden bg-hero">
        <img
          src={aboutHeroBg}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/75 to-background pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="container mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">
              About Us
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-6">
              An Alabama government affairs firm built for the work that moves{" "}
              <span className="text-gradient-primary">communities forward</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {introP1}
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed mt-4">
              {introP2}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Origin Story Timeline */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">
              Our Story
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-4">
              How <span className="text-gradient-primary">The Emerging Market</span> began
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Born from a simple observation: Alabama's most important work was happening without a government affairs partner in the room.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto relative">
            {/* Horizontal line */}
            <div className="absolute left-0 right-0 top-[2.25rem] h-px bg-border hidden md:block" />
            {/* Vertical line for mobile */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-border md:hidden" />

            <div className="md:grid md:grid-cols-2 md:gap-12">
              {[
                {
                  icon: Lightbulb,
                  year: "The Spark",
                  title: "A Gap We Couldn't Ignore",
                  description:
                    "Small, community-rooted organizations were getting outmatched at the State House — not for lack of ideas, but for lack of representation. Lobbyists, strategists, and researchers were available only to clients who could afford them.",
                },
                {
                  icon: Rocket,
                  year: "Today",
                  title: "A Firm Built for Them",
                  description:
                    "We now serve clients across Alabama as their government affairs team — lobbying, communicating, organizing, researching, and securing capital on their behalf, with live infrastructure that keeps the work transparent.",
                },
              ].map((milestone, i) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="relative pl-16 md:pl-0 md:text-center mb-12 md:mb-0"
                >
                  {/* Dot */}
                  <div className="absolute left-3.5 top-1 md:left-1/2 md:-translate-x-1/2 md:top-5 w-5 h-5 rounded-full border-2 border-primary bg-background z-10" />

                  <div className="md:pt-14">
                    <span className="text-primary text-xs font-bold tracking-wider uppercase">
                      {milestone.year}
                    </span>
                    <h3 className="font-display text-lg font-bold mt-1 mb-2">
                      {milestone.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
                      {milestone.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>


      <section className="py-20 bg-card border-y border-border">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              How we work
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Three principles guide every engagement, regardless of the practice area.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pillars.map((pillar, i) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                  <pillar.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-display text-xl font-bold mb-2">
                  {pillar.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">
              Our Practice
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-4">
              Five disciplines under{" "}
              <span className="text-gradient-primary">one roof</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Most government affairs firms specialize in one or two of these. Our clients get all five — coordinated, and grounded in Alabama data.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Practice Deep Dives */}
      {practices.map((group, groupIdx) => (
        <section
          key={group.title}
          className={`py-20 ${groupIdx % 2 === 0 ? "bg-card border-y border-border" : "bg-background"}`}
        >
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex flex-col md:flex-row md:items-start gap-6 mb-10"
              >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <group.icon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <span className="text-primary text-xs font-bold tracking-[0.2em] uppercase block mb-1">
                    Practice 0{groupIdx + 1}
                  </span>
                  <h2 className="font-display text-2xl md:text-3xl font-bold mb-1">
                    {group.title}
                  </h2>
                  <p className="text-primary text-sm font-semibold mb-3">
                    {group.tagline}
                  </p>
                  <p className="text-muted-foreground leading-relaxed max-w-2xl">
                    {group.description}
                  </p>
                </div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {group.services.map((service, i) => (
                  <motion.div
                    key={service.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className={`${groupIdx % 2 === 0 ? "bg-background" : "bg-card"} border border-border rounded-lg p-6 hover:border-primary/40 hover:shadow-glow transition-all duration-300`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <service.icon className="h-4 w-4 text-primary" />
                    </div>
                    <h3 className="font-display text-base font-bold mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-24 bg-background border-t border-border">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              Ready to put us{" "}
              <span className="text-gradient-primary">in the room</span>?
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Get started with our team. We'll walk through the bills, coalitions, research, and capital relevant to your work — and where we'd plug in.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="mailto:info@emrkt.co"
                className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-8 py-3 text-sm font-semibold shadow-glow hover:bg-primary/90 transition-colors"
              >
                Get Started <ArrowRight className="ml-2 h-4 w-4" />
              </a>
              <a
                href="mailto:info@emrkt.co"
                className="inline-flex items-center justify-center rounded-md border border-border bg-background text-foreground px-8 py-3 text-sm font-semibold hover:bg-accent transition-colors"
              >
                Engage Our Firm
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default AboutUs;
